import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";

const PasswordStrength = ({ password }) => {
  const { t } = useTranslation();
  
  const requirements = useMemo(() => {
    return [
      {
        label: t('password.requirements.minLength'),
        met: password.length >= 8,
      },
      {
        label: t('password.requirements.uppercase'),
        met: /[A-Z]/.test(password),
      },
      {
        label: t('password.requirements.lowercase'),
        met: /[a-z]/.test(password),
      },
      {
        label: t('password.requirements.number'),
        met: /[0-9]/.test(password),
      },
      {
        label: t('password.requirements.special'),
        met: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
      },
    ];
  }, [password, t]);

  const strength = useMemo(() => {
    const metCount = requirements.filter((r) => r.met).length;
    if (metCount === 0) return { level: 0, label: "", color: "bg-slate-200" };
    if (metCount <= 2) return { level: 1, label: t('password.strength.weak'), color: "bg-red-500" };
    if (metCount <= 3) return { level: 2, label: t('password.strength.medium'), color: "bg-orange-500" };
    if (metCount <= 4) return { level: 3, label: t('password.strength.good'), color: "bg-yellow-500" };
    return { level: 4, label: t('password.strength.excellent'), color: "bg-green-500" };
  }, [requirements, t]);

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2" data-testid="password-strength">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden flex gap-0.5" data-testid="password-strength-bar">
          {[1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className={`flex-1 h-full transition-colors duration-300 ${
                level <= strength.level ? strength.color : "bg-slate-200"
              }`}
            />
          ))}
        </div>
        {strength.label && (
          <span
            data-testid="password-strength-label"
            className={`text-xs font-medium ${
              strength.level <= 1
                ? "text-red-600"
                : strength.level === 2
                ? "text-orange-600"
                : strength.level === 3
                ? "text-yellow-600"
                : "text-green-600"
            }`}
          >
            {strength.label}
          </span>
        )}
      </div>

      {/* Requirements checklist */}
      <div className="grid grid-cols-2 gap-1" data-testid="password-requirements">
        {requirements.map((req) => (
          <div
            key={req.label}
            data-testid={`strength-requirement-${req.label}`}
            className={`flex items-center gap-1.5 text-xs ${
              req.met ? "text-green-600" : "text-slate-400"
            }`}
          >
            {req.met ? (
              <Check className="w-3 h-3" />
            ) : (
              <X className="w-3 h-3" />
            )}
            <span>{req.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrength;
